import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import {
  parseISO,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  startOfDay,
  isToday,
  isYesterday,
  format,
} from "date-fns"
import { getCaseClassificationBadges } from "./civilTaxonomy"
import {
  normalizeWorkflowStatus,
  type WorkflowStatus,
} from './workflowStatus';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Display-only: normalizes Vs and shortens multi-party suffixes to "etc" (no periods). */
export function formatCaseTitle(title: string | undefined | null): string {
  if (title == null || title === '') return '';
  const text = typeof title === 'string' ? title : String(title);
  return text
    .replace(/\s+vs\.?\s+/gi, '  Vs  ')
    .replace(/\s+and\s+others\b/gi, ' etc')
    .replace(/\s+and\s+another\b/gi, ' etc')
    .replace(/\betc\./gi, 'etc');
}

export type ParsedCaseTitle =
  | { single: string }
  | { before: string; after: string };

export function parseCaseTitleLines(
  title: string | undefined | null,
): ParsedCaseTitle {
  const trimmed = title?.trim();
  if (!trimmed) return { single: '' };

  const match = trimmed.match(/^(.+?)\s+vs\.?\s+(.+)$/i);
  if (match) {
    return { before: match[1].trim(), after: match[2].trim() };
  }
  return { single: trimmed };
}

export function generateDocumentFilename(docTitle: string | undefined | null, caseTitle: string | undefined | null, extension: string = 'docx'): string {
  const cleanDoc = (docTitle || '').trim();
  let cleanCase = (caseTitle || '').trim();

  // If the user's document title already includes the case title (e.g. "Application - Saad vs Saad"), 
  // we don't want to repeat " - Saad vs Saad".
  let finalTitle = cleanDoc;
  
  if (cleanCase && cleanCase !== cleanDoc) {
    // Check if the cleanDoc already includes cleanCase
    if (!cleanDoc.toLowerCase().includes(cleanCase.toLowerCase())) {
        finalTitle = `${cleanDoc} - ${cleanCase}`;
    }
  }

  // Deduplicate any repeated segments "Foo - Foo"
  const parts = finalTitle.split(' - ');
  if (parts.length >= 2) {
    // Only keeping unique parts in order
    const uniqueParts: string[] = [];
    parts.forEach(p => {
      const sp = p.trim();
      if (sp && !uniqueParts.some(u => u.toLowerCase() === sp.toLowerCase())) {
        uniqueParts.push(sp);
      }
    });
    finalTitle = uniqueParts.join(' - ');
  }

  // Fallback defaults
  if (!finalTitle) {
    finalTitle = "LexGat Document";
  }

  // Strip existing explicit "LexGat" or ".docx" to avoid mess, we append cleanly
  finalTitle = finalTitle.replace(/\s*\|\s*LexGat\s*$/i, '');
  finalTitle = finalTitle.replace(/\.pdf$/i, '');
  finalTitle = finalTitle.replace(/\.docx$/i, '');
  
  // Safe filename character replacement
  finalTitle = finalTitle.replace(/[/\\?%*:|"<>]/g, '-').replace(/\s+/g, ' ');

  // Add LexGat cleanly
  return `${finalTitle.trim()} LexGat.${extension.replace(/^\./, '')}`;
}

export function safeParseDate(val: any): Date {
  if (!val) return new Date();
  if (val instanceof Date) return val;
  if (val && typeof val.toDate === 'function') return val.toDate(); // Firestore timestamp
  if (typeof val === 'number') return new Date(val); // Unix timestamp
  if (typeof val === 'string') {
    try {
      const parsed = parseISO(val);
      if (isNaN(parsed.getTime())) return new Date();
      return parsed;
    } catch {
       return new Date();
    }
  }
  return new Date();
}

/** Groups items by Today / Yesterday / Previous 7 Days / month — merges non-adjacent matches. */
export function groupByRecencyLabel<T>(
  items: T[],
  getDateStr: (item: T) => string | undefined | null,
): { label: string; items: T[] }[] {
  const labelOrder: string[] = [];
  const byLabel = new Map<string, T[]>();
  const now = startOfDay(new Date());

  for (const item of items) {
    const dateStr = getDateStr(item);
    let label = 'Older';

    if (dateStr) {
      const date = safeParseDate(dateStr);
      const dateStart = startOfDay(date);
      const diffDays = differenceInDays(now, dateStart);

      if (isToday(date)) {
        label = 'Today';
      } else if (isYesterday(date)) {
        label = 'Yesterday';
      } else if (diffDays <= 7) {
        label = 'Previous 7 Days';
      } else {
        label = format(date, 'MMMM yyyy');
      }
    }

    if (!byLabel.has(label)) {
      labelOrder.push(label);
      byLabel.set(label, []);
    }
    byLabel.get(label)!.push(item);
  }

  return labelOrder.map((label) => ({ label, items: byLabel.get(label)! }));
}

/** Parse draft timestamps; returns null when missing or invalid. */
export function parseDraftTimestamp(val: unknown): Date | null {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  if (val && typeof (val as { toDate?: () => Date }).toDate === "function") {
    const d = (val as { toDate: () => Date }).toDate();
    return isNaN(d.getTime()) ? null : d;
  }
  if (val && typeof (val as { toMillis?: () => number }).toMillis === "function") {
    const d = new Date((val as { toMillis: () => number }).toMillis());
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof val === "number") {
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof val === "string") {
    try {
      const parsed = parseISO(val);
      return isNaN(parsed.getTime()) ? null : parsed;
    } catch {
      return null;
    }
  }
  return null;
}

/** Tiered relative label for draft cards: minutes → hours → today → yesterday → date. */
export function formatDraftEditedAt(date: Date | null): string {
  if (!date) return "Edited recently";

  const now = new Date();
  const minutes = differenceInMinutes(now, date);
  const hours = differenceInHours(now, date);

  if (minutes < 1) return "Edited just now";
  if (minutes < 60) {
    return `Edited ${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }
  if (hours < 24) {
    return `Edited ${hours} hour${hours === 1 ? "" : "s"} ago`;
  }
  if (isToday(date)) return "Edited today";
  if (isYesterday(date)) return "Edited yesterday";
  return `Edited ${format(date, "MMM d, yyyy")}`;
}

export type DraftCardDisplay = {
  documentTitle: string;
  linkedCaseTitleRaw: string | null;
  caseTypeLabel: string | null;
  editedLabel: string;
  nextDateLabel: string;
  nextHearingDateRaw: string | null;
  workflowStatus: WorkflowStatus | null;
};

function isHumanReadableDocumentType(documentType: string | undefined): boolean {
  if (!documentType) return false;
  if (documentType === "Blank Document") return false;
  if (documentType.startsWith("custom-")) return false;
  return true;
}

/** Removes a linked case title suffix from a stored draft title (legacy `Doc - Case` patterns). */
export function stripCaseSuffixFromDocumentTitle(
  documentTitle: string | undefined | null,
  caseTitle?: string | null,
): string {
  if (!documentTitle?.trim()) return '';
  const trimmed = documentTitle.trim();
  if (!caseTitle?.trim()) return trimmed;

  const rawCase = caseTitle.trim();
  const formattedCase = formatCaseTitle(rawCase);
  const separators = [' - ', ' | '];
  const caseVariants = [rawCase, formattedCase].filter(
    (v, i, arr) => v && arr.indexOf(v) === i,
  );

  for (const sep of separators) {
    for (const caseName of caseVariants) {
      const suffix = `${sep}${caseName}`;
      if (trimmed.endsWith(suffix)) {
        const stripped = trimmed.slice(0, -suffix.length).trim();
        if (stripped) return stripped;
      }
    }
  }

  return trimmed;
}

/** Workspace tabs bar: document name + linked case for disambiguation. */
export function formatWorkspaceDraftTabTitle(
  documentTitle: string | undefined | null,
  caseTitle?: string | null,
): string {
  const rawCase = caseTitle?.trim();
  const formattedCase = rawCase ? formatCaseTitle(rawCase) : '';
  const docPart =
    stripCaseSuffixFromDocumentTitle(documentTitle, formattedCase || rawCase) ||
    documentTitle?.trim() ||
    'Draft';
  if (formattedCase && formattedCase.toLowerCase() !== docPart.toLowerCase()) {
    return `${docPart} · ${formattedCase}`;
  }
  return docPart;
}

/** Tab label in the editor sidebar / download menu (no linked case suffix). */
export function getTabDisplayTitle(
  tabTitle: string | undefined | null,
  caseTitle?: string | null,
): string {
  const stripped = stripCaseSuffixFromDocumentTitle(tabTitle, caseTitle);
  return stripped || tabTitle?.trim() || 'Untitled';
}

/** Document name only (no linked case title) for cards and editor. */
export function resolveDocumentTitle(
  draft: { title?: string; documentType?: string; caseId?: string },
  casesMap: Record<string, string>,
): string {
  const rawCaseName = draft.caseId ? casesMap[draft.caseId] : undefined;

  if (draft.title?.trim()) {
    const stripped = stripCaseSuffixFromDocumentTitle(draft.title, rawCaseName);
    if (stripped) return stripped;
  }

  if (isHumanReadableDocumentType(draft.documentType)) {
    return draft.documentType!;
  }
  if (draft.documentType?.trim()) return draft.documentType.trim();
  return 'Untitled Document';
}

function resolveCaseTypeLabel(
  caseId: string | undefined,
  casesById: Record<string, Record<string, unknown>>,
): string | null {
  if (!caseId || !casesById[caseId]) return "Unlinked";
  const badges = getCaseClassificationBadges(casesById[caseId]);
  if (badges.length === 0) return null;
  if (badges.length === 1) return badges[0];
  return `${badges[0]} · ${badges[1]}`;
}

export function getDraftCardDisplay(
  draft: {
    title?: string;
    documentType?: string;
    caseId?: string;
    type?: string;
    workflowStatus?: string;
    updatedAt?: unknown;
    createdAt?: unknown;
  },
  casesMap: Record<string, string>,
  casesById: Record<string, Record<string, unknown>>,
): DraftCardDisplay {
  const caseRaw = draft.caseId ? casesMap[draft.caseId] : undefined;
  const caseRecord = draft.caseId ? casesById[draft.caseId] : undefined;
  const nextHearingDate = caseRecord?.nextHearingDate as string | undefined;
  const editedAt =
    parseDraftTimestamp(draft.updatedAt) ?? parseDraftTimestamp(draft.createdAt);
  const workflowStatus =
    draft.type === 'template'
      ? null
      : normalizeWorkflowStatus(draft.workflowStatus);

  return {
    documentTitle: resolveDocumentTitle(draft, casesMap),
    linkedCaseTitleRaw: caseRaw ?? null,
    caseTypeLabel: resolveCaseTypeLabel(draft.caseId, casesById),
    editedLabel: formatDraftEditedAt(editedAt),
    nextDateLabel: nextHearingDate
      ? format(safeParseDate(nextHearingDate), 'd MMM yyyy')
      : 'Not Scheduled',
    nextHearingDateRaw: nextHearingDate ?? null,
    workflowStatus,
  };
}
